import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import { styled } from '@mui/system'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {
  DialogButton,
  DialogContentText,
  useDialogButtonRef,
} from '../../../components/dialogs'
import { Checkbox } from '../../../components/inputs/Checkbox'
import { intl } from '../../../../i18n'
import { colorPalette } from '../../../style/colorPalette'
import { FontSize, FontWeight } from '../../../../styles/commonStyles'

export type RoleChangeItem = {
  userUuid: string
  userName: string
  oldRoleName: string
  newRoleName: string
}

type RoleChangeConfirmationDialogProps = {
  open: boolean
  roleChanges: RoleChangeItem[]
  selectedUserUuids: Set<string>
  onToggleUser: (userUuid: string) => void
  onToggleAll: () => void
  onConfirm: () => void
  onCancel: () => void
}

export const RoleChangeConfirmationDialog = ({
  open,
  roleChanges,
  selectedUserUuids,
  onToggleUser,
  onToggleAll,
  onConfirm,
  onCancel,
}: RoleChangeConfirmationDialogProps) => {
  const [buttonRef, focusButton, blurButton] = useDialogButtonRef()

  const allSelected = selectedUserUuids.size === roleChanges.length
  const noneSelected = selectedUserUuids.size === 0
  const isIndeterminate = !allSelected && !noneSelected

  return (
    <Dialog
      open={open}
      TransitionProps={{
        onEnter: focusButton,
      }}
      onClose={onCancel}
      maxWidth={false}
      PaperProps={{ sx: { maxWidth: 1000 } }}
    >
      <DialogTitle>
        {intl.formatMessage({ id: 'user.roleChange.dialog.title' })}
      </DialogTitle>
      <DialogContent dividers={true}>
        <DialogContentText>
          {intl.formatMessage({ id: 'user.roleChange.dialog.message' })}
        </DialogContentText>
        <NoteText>
          {intl.formatMessage({ id: 'user.roleChange.dialog.pmNote' })}
        </NoteText>
        <Table>
          <HeaderRow>
            <CheckboxCell>
              <Checkbox
                checked={allSelected}
                indeterminate={isIndeterminate}
                onChange={onToggleAll}
                size="s"
              />
            </CheckboxCell>
            <UserNameCell>
              {intl.formatMessage({
                id: 'user.roleChange.dialog.columnUser',
              })}
            </UserNameCell>
            <RoleCell>
              {intl.formatMessage({
                id: 'user.roleChange.dialog.columnBeforeRole',
              })}
            </RoleCell>
            <ArrowCell />
            <RoleCell>
              {intl.formatMessage({
                id: 'user.roleChange.dialog.columnAfterRole',
              })}
            </RoleCell>
          </HeaderRow>
          <TableBody>
            {roleChanges.map(item => (
              <BodyRow key={item.userUuid}>
                <CheckboxCell>
                  <Checkbox
                    checked={selectedUserUuids.has(item.userUuid)}
                    onChange={() => onToggleUser(item.userUuid)}
                    size="s"
                  />
                </CheckboxCell>
                <UserNameCell>{item.userName}</UserNameCell>
                <RoleCell>{item.oldRoleName}</RoleCell>
                <ArrowCell>
                  <StyledArrowIcon />
                </ArrowCell>
                <RoleCell>{item.newRoleName}</RoleCell>
              </BodyRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <DialogButton onClick={onConfirm} color="primary" ref={buttonRef}>
          {intl.formatMessage({ id: 'dialog.ok' })}
        </DialogButton>
        <DialogButton
          onClick={onCancel}
          color="primary"
          onMouseOver={blurButton}
        >
          {intl.formatMessage({ id: 'dialog.cancel' })}
        </DialogButton>
      </DialogActions>
    </Dialog>
  )
}

const NoteText = styled('p')({
  fontSize: FontSize.SMALL,
  color: colorPalette.monotone[4],
  margin: '4px 0 12px 5px',
})

const Table = styled('div')({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: 4,
  overflow: 'hidden',
})

const Row = styled('div')({
  display: 'flex',
  alignItems: 'center',
  padding: '6px 16px',
  gap: 12,
})

const HeaderRow = styled(Row)({
  backgroundColor: colorPalette.monotone[1],
  fontWeight: FontWeight.BOLD,
  fontSize: FontSize.SMALL,
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  overflowY: 'auto',
  scrollbarGutter: 'stable',
})

const TableBody = styled('div')({
  maxHeight: 400,
  overflowY: 'auto',
  scrollbarGutter: 'stable',
})

const BodyRow = styled(Row)({
  fontSize: FontSize.MEDIUM,
  borderBottom: `1px solid ${colorPalette.monotone[1]}`,
  '&:last-child': {
    borderBottom: 'none',
  },
})

const UserNameCell = styled('div')({
  flex: '1 1 250px',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const RoleCell = styled('div')({
  flex: '0 0 200px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const ArrowCell = styled('div')({
  flex: '0 0 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const CheckboxCell = styled('div')({
  flex: '0 0 40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const StyledArrowIcon = styled(ArrowForwardIcon)({
  fontSize: 14,
  color: colorPalette.monotone[3],
})
